import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';
import './DragAndDrop.css';

const DragAndDrop = ({
  onFileChange,
  fileInputRef,
  promptText = 'Drag & drop your PDF files here or',
  buttonText = 'Browse Files',
  isExistingTransaction = false,
  disabled = false,
  existingFiles = [], // Array of existing file objects with 'name' property
}) => {
  const [isDragging, setIsDragging] = useState(false);

  // Prevent default drag behavior on the entire page
  useEffect(() => {
    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDocumentDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Only prevent if the drop is outside our component
      if (!e.target.closest('.drop-zone')) {
        return false;
      }
    };

    // Add event listeners to prevent default browser behavior
    document.addEventListener('dragenter', preventDefaults, false);
    document.addEventListener('dragover', preventDefaults, false);
    document.addEventListener('dragleave', preventDefaults, false);
    document.addEventListener('drop', handleDocumentDrop, false);

    return () => {
      // Clean up event listeners
      document.removeEventListener('dragenter', preventDefaults, false);
      document.removeEventListener('dragover', preventDefaults, false);
      document.removeEventListener('dragleave', preventDefaults, false);
      document.removeEventListener('drop', handleDocumentDrop, false);
    };
  }, []);

  const handleDragOver = (event) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (event) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (event) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      validateAndProcessFiles(files);
    }
  };

  const handleFileInputChange = (event) => {
    if (disabled) return;
    const files = event.target.files;
    if (files.length > 0) {
      validateAndProcessFiles(files);
    }
  };

  const validateAndProcessFiles = (selectedFiles) => {
    // Check if all files are PDFs
    if (!selectedFiles.length || !Array.from(selectedFiles).every(file => file.type === 'application/pdf')) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid File',
        text: 'Please upload only PDF files.',
      });
      return;
    }

    // Check for maximum file limit
    if (selectedFiles.length > 10) {
      Swal.fire({
        icon: 'error',
        title: 'Too Many Files',
        text: 'You can upload a maximum of 10 files at a time.',
      });
      return;
    }

    // Check for duplicate files (only if existingFiles is provided and not empty)
    if (existingFiles && existingFiles.length > 0) {
      const existingFileNames = existingFiles.map(file => file.name.toLowerCase());
      const duplicateFiles = [];
      const uniqueFiles = [];

      Array.from(selectedFiles).forEach(file => {
        if (existingFileNames.includes(file.name.toLowerCase())) {
          duplicateFiles.push(file.name);
        } else {
          uniqueFiles.push(file);
        }
      });

      // If there are duplicate files, show warning
      if (duplicateFiles.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Duplicate Files Detected',
          html: `
            <div style="text-align: left;">
              <p>The following file(s) are already uploaded:</p>
              <ul style="margin-top: 10px; padding-left: 20px;">
                ${duplicateFiles.map(name => `<li>${name}</li>`).join('')}
              </ul>
              ${uniqueFiles.length > 0 ? '<p style="margin-top: 10px;">Other files will be uploaded.</p>' : ''}
            </div>
          `,
          confirmButtonText: 'OK',
        });

        // If there are unique files, proceed with them
        if (uniqueFiles.length > 0) {
          onFileChange(uniqueFiles);
        }
        return;
      }
    }

    // All validations passed, proceed with upload
    onFileChange(selectedFiles);
  };

  const handleClick = (event) => {
    if (disabled) return;
    event.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current.click();
  };

  return (
    <Box
      className={`dropzone ${isDragging && !disabled ? 'active' : ''} ${isExistingTransaction ? 'existing-transaction' : ''} ${disabled ? 'disabled' : ''}`}
      onDragOver={disabled ? null : handleDragOver}
      onDragEnter={disabled ? null : handleDragEnter}
      onDragLeave={disabled ? null : handleDragLeave}
      onDrop={disabled ? null : handleDrop}
      onClick={disabled ? null : handleClick}
    >
      <Typography variant="body1" className="dropzone-text">
        {promptText}
      </Typography>
      {isExistingTransaction && (
        <Typography variant="caption" className="dropzone-subtext">
          (Supports multiple PDF files)
        </Typography>
      )}
      <Button
        variant="contained"
        className="btn-contained upload-button"
        onClick={handleClick}
        disabled={disabled}
      >
        {buttonText}
      </Button>
      <input
        type="file"
        accept="application/pdf"
        multiple
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
        disabled={disabled}
      />
    </Box>
  );
};

DragAndDrop.propTypes = {
  onFileChange: PropTypes.func.isRequired,
  fileInputRef: PropTypes.object.isRequired,
  promptText: PropTypes.string,
  buttonText: PropTypes.string,
  isExistingTransaction: PropTypes.bool,
  disabled: PropTypes.bool,
  existingFiles: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
    })
  ),
};

export default DragAndDrop;